import * as React from 'react';
import { ViewType } from '../../enums/ViewType';
import './view.scss';
import { Filters } from '../Filters/filters';
import { Add, Primary } from 'rcd-cpp-ui/ui/button';
import { useActions } from '../MetadataForm/metadataFormActions';
import { Table } from './Tabular/tabular';
import { Report, ReportTools } from './Report/report';
import { useActions as useViewActions } from './viewActions';
import { useActions as usePlanningActions } from '../ContentPlanning/contentPlanningActions';
import { Info } from './Info/info';
import {
  AdministrationTable,
  AdministrationTools
} from '../Administration/administration';
import AccessControl from '../AccessControl/accessControl';
import { Export } from './Export/export';
import { ViewDescription } from './ViewDescription/viewDescription';
import { ActionMenu } from './ActionMenu/actionMenu';
import { useActions as useAdminActions } from '../Administration/administrationActions';
import URLManager from '../../utils/urlManager';
import { useToaster } from '../Toaster/toaster';
import { Board } from './Board/board';
import { Dropbox } from '../Dropbox/dropbox';
import { MultiSort } from '../MultiSort/multiSort';
import { excludeEmptyFilters, getAllViews, getIntersection } from '../../utils';
import { useSelector } from '../../utils/useSelector';
import { useSearchParams } from 'react-router-dom';
import { useFullStory } from '../App/fullStory';
import { TriggerLocation } from '../../enums/TriggerLocation';
import { FSEvent } from '../../enums/FSEvent';
import { BackupMode } from './BackupMode/backupMode';
import { deepEquals } from 'rcd-cpp-workspace/utils/deepEquals';
import { ViewAsMode } from './ViewAsMode/viewAsMode';
import { Role } from '../../enums/Role';
import { JobRole } from '../../enums/JobRole';
import { AdminDomain } from '../../enums/AdminDomain';
import { EditMode } from './EditMode/editMode';
import { EFSEditMode } from './EFSViewComponents/EFSEditMode/EFSEditMode';
import { EFSBuilderTools } from './EFSViewComponents/EFSBuilderTools/EFSBuilderTools';
import { PersonalizeView } from '../PersonalizeView/personalizeView';
import { shouldShowEditModeButton } from '../../utils/editModeUtils';
import { ViewCategory } from '../../enums/viewCategory';

const Views = {
  [ViewType.AdministrationTable]: AdministrationTable,
  [ViewType.Report]: Report,
  [ViewType.Kanban]: Board,
  [ViewType.Tabular]: Table
};

const Tools = {
  [ViewType.AdministrationTable]: AdministrationTools,
  [ViewType.Report]: ReportTools
};

export const View = ({ page }: { page?: AdminDomain }) => {
  const editMode = useSelector(s => s.view?.editMode);
  const showCheckbox = shouldShowCheckbox();
  const selected = useSelector(s => {
    const selected = s.view?.selected;
    if (selected?.props?.type == ViewType.AdministrationTable) {
      return selected;
    }
    const allViews = getAllViews(s.planning?.selectedWorkspace);
    if (
      selected &&
      allViews.filter(v => v.props.refId == selected?.props.refId).length == 0
    ) {
      return null;
    }
    return selected;
  });
  const firstViewId = useSelector(s => {
    const selected = s.view?.selected;
    if (selected?.props?.type != ViewType.AdministrationTable) {
      const allViews = getAllViews(s.planning?.selectedWorkspace);
      if (
        selected &&
        allViews.filter(v => v.props.refId == selected?.props.refId).length == 0
      ) {
        return allViews[0].props?.refId;
      }
    }
  });
  const selectedViewId = selected?.props?.refId;
  const workspace = useSelector(s => s.planning?.selectedWorkspace);
  const selectedWorkspaceId = workspace?.props?.refId;
  const { selectWorkspace, saveSelectedWorkspaceView } = usePlanningActions();
  const { viewAdminData } = useAdminActions();
  const { wid, vid, updateSearchParams } = URLManager.useRouter();
  const { hideToasters } = useToaster();
  const { captureEvent } = useFullStory();
  const { disableEditMode } = useViewActions();
  React.useEffect(() => {
    disableEditMode();
  }, []);
  React.useEffect(() => {
    hideToasters();
    if (selectedViewId != vid) {
      saveSelectedWorkspaceView(wid, vid);
    }
  }, [vid]);
  React.useEffect(() => {
    if (!page && selectedWorkspaceId == wid) {
      if (selectedViewId) {
        if (vid && selectedViewId != vid) {
          selectWorkspace(wid, vid);
        } else if (!vid) {
          updateSearchParams({ v: selectedViewId });
        }
      } else if (vid && firstViewId) {
        updateSearchParams({ v: firstViewId });
        selectWorkspace(wid, firstViewId);
      } else if (vid && !firstViewId && selected === undefined) {
        selectWorkspace(wid, vid);
      }
    }
  }, [wid, vid, selectedViewId, workspace]);
  React.useEffect(() => {
    if (selectedViewId && vid == selectedViewId) {
      captureEvent(FSEvent.VisitView);
    }
  }, [vid, selectedViewId]);

  React.useEffect(() => {
    if (selectedWorkspaceId && !!page) {
      viewAdminData(page);
    }
  }, [page, selectedWorkspaceId]);

  let SelectedView = Views[selected?.props?.type];
  const Tool = selected?.props?.isEFSBuilder
    ? EFSBuilderTools
    : Tools[selected?.props?.type] || ViewTools;
  if (!selectedWorkspaceId) return null;
  return (
    <div className="view">
      <div className="view-info">
        <div className="view-description">
          <ViewDescription />
        </div>
        <BackupMode />
        <ViewAsMode />
        {editMode &&
          (selected?.props?.isEFSBuilder ? <EFSEditMode /> : <EditMode />)}
      </div>
      <AccessControl resource={selected?.dataSchema}>
        <Tool showCheckbox={showCheckbox} />
      </AccessControl>
      <Info />
      <ViewCount viewType={selected?.props?.type} />
      {SelectedView && (
        <AccessControl resource={selected?.dataSchema}>
          <SelectedView key={selected?.id} showCheckbox={showCheckbox} />
        </AccessControl>
      )}
    </div>
  );
};

const ViewCount = ({ viewType }) => {
  const count = useSelector(s => s.view?.info?.count);
  const selected = useSelector(s => {
    const count = s.view?.info?.count;
    if (!Number.isInteger(count)) return 0;
    const isCheckedAll = s.view?.isCheckedAll;
    const toggledRefIds = s.view?.toggledRefIds;
    const toggled = toggledRefIds?.length || 0;
    if (!isCheckedAll) return toggled;
    const refIds = s.view?.info?.refIds;
    if (refIds && toggled) {
      return refIds.filter(i => !toggledRefIds.includes(i)).length;
    }
    return count - toggled;
  });
  if (ViewType.Kanban == viewType) return <BoardViewCount />;
  //if (ViewType.Report == viewType) return null;
  if (!Number.isInteger(count)) return null;
  const resultCount = toCountResult(count);

  const selectedCount = selected
    ? `, ${selected.toLocaleString()} selected`
    : '';
  return (
    <div className="view-count">
      {resultCount}
      {selectedCount}
    </div>
  );
};

const BoardViewCount = () => {
  const groups = useSelector(s => s.view.groups);
  const data = useSelector(s => s.view.data);
  const hasGroups = groups?.length > 0;
  const retrievalTime = useSelector(s => s.view.info?.retrievalTime);
  const ref = React.useRef(null);
  const [count, setCount] = React.useState(null);
  const { getBoardCardCount } = useViewActions();
  React.useEffect(() => {
    if (hasGroups && !!retrievalTime) {
      if (ref.current != null) {
        clearTimeout(ref.current);
      }
      ref.current = setTimeout(async () => {
        try {
          const c = await getBoardCardCount();
          setCount(c);
        } catch (e) {
          console.error('Error fetching board count:', e);
          setCount(null);
        }
      }, 300);
    } else {
      setCount(null);
    }
  }, [hasGroups, retrievalTime]);

  if (typeof count == 'number')
    return <div className="view-count">{toCountResult(count)}</div>;
  if (data && !data.length) return <div className="view-count">&nbsp;</div>;
  if (!groups.length) {
    return <div className="view-count">No results found</div>;
  }
  return <div className="view-count">&nbsp;</div>;
};

const toCountResult = (count: number) =>
  count == 0
    ? `No results found`
    : `${count.toLocaleString()} result${count == 1 ? '' : 's'}`;

type VTProps = {
  disabled?: boolean;
  showCheckbox?: boolean;
};

const ViewTools = ({ disabled, showCheckbox }: VTProps) => {
  const selected = useSelector(s => s.view?.selected);
  const sortBy = useSelector(s => s.view?.sortBy);
  const editMode = useSelector(s => s.view?.editMode);
  const newUrlFilters = useSelector(s =>
    URLManager.stringify(excludeEmptyFilters(s.view?.filters))
  );
  const newUrlSortBys = useSelector(s => {
    if (deepEquals(s.view?.sortBy, s.view?.selected?.props?.sortBy))
      return null;
    return URLManager.stringify(s.view?.sortBy?.filter(f => !!f?.field));
  });
  const { showForm } = useActions();
  const { setSortBy, enableEditMode } = useViewActions();
  const [searchParams, setSearchParams] = useSearchParams();
  const curUrlFilters = searchParams.get('f');
  const curUrlSortBys = searchParams.get('s');
  const vid = searchParams.get('v');
  React.useEffect(() => {
    if (!vid) return;
    if (curUrlFilters != newUrlFilters || curUrlSortBys != newUrlSortBys) {
      if (curUrlFilters != newUrlFilters) {
        if (!newUrlFilters) searchParams.delete('f');
        else searchParams.set('f', newUrlFilters);
      }
      if (curUrlSortBys != newUrlSortBys) {
        if (!newUrlSortBys) searchParams.delete('s');
        else searchParams.set('s', newUrlSortBys);
      }
      setSearchParams(searchParams);
    }
  }, [newUrlFilters, newUrlSortBys]);
  if (!selected) return null;
  return (
    <div className={'view-tools'}>
      <ViewFilters />
      <div>
        <Dropbox
          className="sort-by"
          renderBox={() => (
            <div className="sort-options">
              <MultiSort
                dataSchema={selected.dataSchema}
                sortBy={sortBy}
                viewFields={selected.props?.fields}
                setSortBy={setSortBy}
              />
            </div>
          )}
          position="right"
        >
          <SortIcon />
          &nbsp;sort
        </Dropbox>
        <PersonalizeView />
        {shouldShowEditModeButton(selected, editMode) && (
          <Primary
            className="edit-mode-button view-button"
            buttonSize="small"
            label="EDIT MODE"
            onClick={() => enableEditMode()}
          />
        )}
        {!disabled && (
          <>
            {selected?.category != ViewCategory.Views && (
              <Add
                buttonSize="small"
                className="view-button"
                onClick={() =>
                  showForm({ triggerLocation: TriggerLocation.VIEW })
                }
              />
            )}
          </>
        )}
        <ActionMenu showCheckbox={showCheckbox} />
      </div>
    </div>
  );
};

const ViewFilters = () => {
  const viewRefId = useSelector(s => s.view?.selected?.props?.refId);
  const viewFilters = useSelector(s => s.view?.selected?.props?.filters);
  const getAPI = useSelector(s => s?.app?.elFn?.getAPI);
  const filters = useSelector(s => s.view?.filters);
  const preappliedFilters = useSelector(
    s => s?.view?.selected?.props?.preApplyFilters
  );
  const schemas = useSelector(s => s.planning?.selectedWorkspace?.schemas);
  const defaultFilters = useSelector(
    s => s.planning?.selectedWorkspace?.setting?.props?.defaultFilters
  );
  const { setFilter, setFilters, removeFilter, addFilter, addAnotherFilter } =
    useViewActions();

  const requiredFilters = (viewFilters || []).reduce((r, f) => {
    if (f.isRequired) r[f.field] = true;
    return r;
  }, {});
  filters.forEach(f => {
    if (requiredFilters[f.field]) {
      f.isRequired = true;
      requiredFilters[f.field] = false;
    }
  });
  return (
    <Filters
      getAPI={getAPI}
      setFilter={setFilter}
      setFilters={setFilters}
      removeFilter={removeFilter}
      addFilter={addFilter}
      addAnotherFilter={addAnotherFilter}
      schemas={schemas}
      filters={filters}
      defaultFilters={defaultFilters}
      preappliedFilters={preappliedFilters}
      viewRefId={viewRefId}
      location="view"
      seeAllFilters={false}
    >
      <Export />
    </Filters>
  );
};

const SortIcon = () => (
  <svg
    width="12"
    height="8"
    viewBox="0 0 12 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M4.66667 8H7.33333V6.66667H4.66667V8ZM0 0V1.33333H12V0H0ZM2 4.66667H10V3.33333H2V4.66667Z"
      fill="#0042AB"
    />
  </svg>
);

// CN-1994
const shouldShowCheckbox = () =>
  useSelector(s => {
    if (!s.view?.selected?.isBacktaggingView) return false;
    const user = s.app?.user;
    if (!user) return false;
    const ws = s.planning?.selectedWorkspace?.setting;
    if (!ws?.props) return false;
    const workspaceRefId = s.planning?.selectedWorkspace?.props?.refId;
    const globalRoles = s.accessControl?.roles || [];
    const workspaceRoles =
      s.accessControl?.permissions?.[workspaceRefId]?.roles || [];
    const allRoles = [...globalRoles, ...workspaceRoles];

    const isAdmin =
      getIntersection(allRoles, [
        Role.admin,
        Role.backtagAdmin,
        Role.developer,
        Role.backtagger
      ]).length > 0;
    if (isAdmin) return true;
    const jobRoles = user.jobRoles || [];
    return (
      !ws.props.lockStagedMetadata &&
      (jobRoles.includes(JobRole.ChiefOfResearch) ||
        jobRoles.includes(JobRole.KILeader))
    );
  });
